import React, { useEffect, useState } from 'react'
import { Input, Button, Select, Row, Col, Space, Tooltip } from 'antd'
import { DeleteOutlined, LinkOutlined } from '@ant-design/icons'

import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'
import { sortBy } from 'lodash'
import { isMobile } from 'react-device-detect'
import { FormatedTimestamp } from '../Shared'
import { useSmartTranslation } from '../../cls/TranslationContext'
/**
 * DynamicFormComponent renders a dynamic list of form entries where each entry
 * consists of a label, a type selector, and an input control based on that type.
 * Supports types: text, password, textarea, link, and nested children arrays.
 * Allows adding, deleting, and reordering entries, and invokes onChange with the updated array.
 *
 * @component
 * @param {Array<Object>} [props.value=[]] – Initial array of inputs, each with { id, sortId, type, label, value, ... }.
 * @param {Function} props.onChange – Callback invoked with the updated inputs array whenever it changes.
 * @param {boolean} [props.viewOnly=false] – When true, renders inputs in read-only mode (no add/delete, link values rendered as anchors).
 * @returns {JSX.Element}
 */
const DynamicFormComponent = ({ value = [], onChange, viewOnly = false }) => {
	const [inputs, setInputs] = useState(() => sortBy(value, 'sortId'))
	const { t } = useSmartTranslation()

	useEffect(() => {
		const sortedInputs = sortBy(value, 'sortId')
		if (JSON.stringify(sortedInputs) !== JSON.stringify(inputs)) {
			setInputs(sortedInputs)
		}
	}, [value]) // Only run effect when 'value' changes
	/**
	 * handleInputChange updates a single field of an input entry by id.
	 * For password fields, it also resets the `encrypted` flag when the value changes.
	 *
	 * @function
	 * @private
	 * @param {string} id – The unique id of the input entry to update.
	 * @param {string} field – The field name to update (e.g. 'value', 'label', 'type').
	 * @param {any} fieldValue – The new value to assign to the field.
	 */
	const handleInputChange = (id, field, fieldValue) => {
		// const newInputs = inputs.map((input) => input.id === id ? { ...input, [field]: fieldValue } : input )

		const newInputs = inputs.map((input) => {
			if (input.id === id) {
				if (field === 'value' && input.type === 'password') {
					return {
						...input,
						value: fieldValue,
						encrypted: false
					}
				}
				return { ...input, [field]: fieldValue }
			}
			return input
		})
		setInputs(newInputs)
		if (onChange) {
			onChange(newInputs)
		}
	}

	const handleNestedChange = (id, newValue) => {
		const newInputs = inputs.map((input) => {
			// input.id === id && console.log('handleNestedChange', input, newValue)
			return input.id === id ? { ...input, value: newValue } : input
		})
		setInputs(newInputs)
		if (onChange) {
			onChange(newInputs)
		}
	}
	/**
	 * addInput appends a new input entry to the list with a fresh UUID and
	 * a sortId one greater than the current maximum. Invokes onChange with the new list.
	 *
	 * @function
	 * @private
	 */
	const addInput = () => {
		const newSortId =
			inputs.length > 0
				? Math.max(...inputs.map((input) => input.sortId)) + 1
				: 1
		const newInput = {
			type: 'text',
			label: '',
			value: '',
			sortId: newSortId,
			id: uuidv4()
		}
		const newInputs = [...inputs, newInput]
		setInputs(newInputs)
		if (onChange) {
			onChange(newInputs)
		}
	}
	/**
	 * deleteInput removes the input entry with the given id from the list.
	 * Invokes onChange with the filtered list.
	 *
	 * @function
	 * @private
	 * @param {string} id – The id of the input entry to remove.
	 */
	const deleteInput = (id) => {
		const newInputs = inputs.filter((input) => input.id !== id)
		setInputs(newInputs)
		if (onChange) {
			onChange(newInputs)
		}
	}
	/**
	 * validateUrl checks whether a given string conforms to a HTTP/HTTPS URL pattern.
	 *
	 * @function
	 * @private
	 * @param {string} value – The URL string to validate.
	 * @returns {boolean} True if the string is a valid URL, false otherwise.
	 */
	const validateUrl = (value) => {
		const urlPattern = /^(https?):\/\/([\w.-]+(\/\S*)?)$/
		return urlPattern.test(value)
	}
	/**
	 * renderInput returns the appropriate AntD input component for an entry
	 * based on its `type` property. Supports:
	 * - 'text' → <Input>
	 * - 'password' → <Input.Password> (with optional encrypted note)
	 * - 'textarea' → <Input.TextArea>
	 * - 'link' → either an <a> in viewOnly or <Input type="url"> with validation
	 * - 'children' → nested <DynamicFormComponent>
	 *
	 * @function
	 * @private
	 * @param {Object} input – The input entry object to render.
	 * @returns {JSX.Element|null}
	 */
	const renderInput = (input) => {
		switch (input.type) {
			case 'text':
				return (
					<Input
						value={
							// input.value could come as array, if is array we set it to empty ''
							typeof input.value === 'string' ? input.value : ''
						}
						onChange={(e) =>
							handleInputChange(input.id, 'value', e.target.value)
						}
					/>
				)
			case 'password':
				return (
					<>
						<Input.Password
							value={
								// input.value could come as array, if is array we set it to empty ''
								typeof input.value === 'string'
									? input.value
									: ''
							}
							onChange={(e) => {
								handleInputChange(
									input.id,
									'value',
									e.target.value
								)
							}}
						/>
						{input.encrypted === true && (
							<Tooltip title={input.id}>
								<p style={{ color: 'red' }}>
									Password was already saved and encrypted on{' '}
									<FormatedTimestamp
										timestamp={input.timestamp}
									/>
									, change will generate new encryption
									triggers.
								</p>
							</Tooltip>
						)}
					</>
				)
			case 'textarea':
				return (
					<Input.TextArea
						value={
							// input.value could come as array, if is array we set it to empty ''
							typeof input.value === 'string' ? input.value : ''
						}
						onChange={(e) =>
							handleInputChange(input.id, 'value', e.target.value)
						}
					/>
				)
			case 'link':
				if (viewOnly) {
					return (
						<a
							href={
								// input.value could come as array, if is array we set it to empty ''
								typeof input.value === 'string'
									? input.value
									: ''
							}
							target="_blank"
							rel="noreferrer"
						>
							<LinkOutlined /> {input.value}
						</a>
					)
				}
				return (
					<Input
						type="url"
						value={input.value}
						onChange={(e) => {
							const value = e.target.value
							handleInputChange(input.id, 'value', value)
							if (!validateUrl(value) && value.length > 8) {
								alert(t('single.invalid.url'))
								handleInputChange(input.id, 'value', '')
							}
						}}
					/>
				)

			case 'children':
				return (
					<DynamicFormComponent
						viewOnly={viewOnly}
						value={
							typeof input.value === 'string' ? [] : input.value
						}
						onChange={(newValue) =>
							handleNestedChange(input.id, newValue)
						}
					/>
				)

			default:
				return null
		}
	}
	/**
	 * deleteButton returns a delete icon button for the given input id.
	 * Clicking it invokes deleteInput to remove that entry.
	 *
	 * @function
	 * @private
	 * @param {string} inputId – The id of the entry to delete.
	 * @returns {JSX.Element}
	 */
	const deleteButton = (inputId) => (
		<DeleteOutlined onClick={() => deleteInput(inputId)} />
	)

	return (
		<Space style={{ width: '100%' }} direction="vertical">
			{inputs.map((input, index) => (
				<Row
					key={input.id}
					gutter={[10, 10]}
					className={
						index % 2 === 0
							? 'dynamic-form-wrapper-bg even'
							: 'dynamic-form-wrapper-bg odd'
					}
				>
					<Col xs={24} sm={4}>
						<Space style={{ width: '100%' }} direction="horizontal">
							{!viewOnly && !isMobile && (
								<Space>
									<span>{index + 1}</span>
									{deleteButton(input.id)}
								</Space>
							)}

							<Input
								style={{
									flex: 'auto'
								}}
								placeholder={t('single.label')}
								value={input.label}
								onChange={(e) =>
									handleInputChange(
										input.id,
										'label',
										e.target.value
									)
								}
							/>

							{!viewOnly && isMobile && (
								<Space>
									<span>{index + 1}</span>
									{deleteButton(input.id)}
								</Space>
							)}
						</Space>
					</Col>
					<Col
						xs={24}
						sm={20}
						className={
							viewOnly
								? 'dynamic-form-wrapper dynamic-form-wrapper-view-mode'
								: 'dynamic-form-wrapper'
						}
					>
						<Select
							key={input.id + '-select'}
							value={input.type}
							onChange={(value) => {
								handleInputChange(input.id, 'type', value)
							}}
							options={[
								{ value: 'text', label: 'Text' },
								{
									value: 'password',
									label: t('single.password.label')
								},
								{
									value: 'textarea',
									label: t('single.textarea')
								},
								{
									value: 'link',
									label: (
										<>
											<LinkOutlined /> Link
										</>
									)
								},
								{
									value: 'children',
									label: t('single.children')
								}
							]}
						/>

						{renderInput(input)}
					</Col>
				</Row>
			))}
			{!viewOnly && (
				<Button
					className="dynamic-form-wrapper-add-input"
					type="dashed"
					onClick={addInput}
				>
					{t('__add%single.input')}
				</Button>
			)}
		</Space>
	)
}

DynamicFormComponent.propTypes = {
	value: PropTypes.any,
	onChange: PropTypes.func,
	viewOnly: PropTypes.bool
}

export default DynamicFormComponent
